/**
 * PATH: packages/kanban-host/src/allocation/AllocateChip.tsx
 * FILE_ID: KANBANHOST-ALLOCATECHIP-001
 * PURPOSE: Click-to-allocate chip for an email that is NOT yet on any
 *          Kanban board. Renders a dashed "Add to board" pill; clicking
 *          it opens a board → lane picker popover and allocates the
 *          email to the chosen lane via `useKanbanQuickAllocate`.
 *
 * WHY:     Operator (2026-06-02): "I cannot click 'not on board' and
 *          allocate a board.. lanes etc". The reader header's
 *          "Not on a board" state was a DEAD label — the only way to
 *          allocate an unallocated email was the right-click
 *          `AllocateToKanbanSubMenu`, which is a different surface and
 *          not discoverable from the open email. This is the inline,
 *          click-driven sibling of that submenu and of `AllocationChip`
 *          (which only handles the ALREADY-allocated case).
 *
 * RELATIONSHIP:
 *   - `AllocationChip`           → allocated email: change lane / move
 *                                  board / accept-reject AI / revoke.
 *   - `AllocateChip` (this)      → unallocated email: pick board + lane
 *                                  to create the first allocation.
 *   - `AllocateToKanbanSubMenu`  → same data, ContextMenu render surface.
 *
 * Reuses the exact same data layer as both siblings:
 *   - `useAllocateToKanbanMenuData` (board buckets)
 *   - `flattenAllocatableBoards` / `filterBoardsByQuery` (pure, from
 *     MoveToBoardPicker)
 *   - `useKanbanBoardLanes` (lane list + default lane)
 *   - `useKanbanQuickAllocate` (the mutation + cache invalidation +
 *     recents bump + toast copy)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import { Kanban, Plus } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'

import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'
import type {
  CardSearchResult,
  CreateBoardInput,
  KanbanApi,
  KanbanAllocationConflict,
  Lane,
} from '@quotech/ui/kanban'
import { CreateBoardDialog, getLaneColor } from '@quotech/ui/kanban'

import { useToast } from '../_internal/notify/useToast'

import {
  useKanbanLinkEmailToCard,
  useKanbanQuickAllocate,
  type AllocateMenuBoard,
} from '../index'
import {
  buildSummaryFromAllocateResult,
  buildSummaryFromLinkResult,
} from './buildAllocationSummary'
import { CardSearchList } from './AllocateChipCardSearch'
import {
  AllocateModeToggle,
  BoardPickList,
  LanePickList,
  type AllocateMode,
} from './AllocateChipLists'
import { ALLOCATION_MENU_Z_CLASS } from './allocationPopoverLayer'
import { useCreateBoardFromPicker } from './useCreateBoardFromPicker'

/** Anchor a fixed-position popover to a trigger rect, flipping above when
 *  there isn't room below and clamping into the viewport. Pure:
 *  (trigger rect, estimated popover size, viewport) → fixed top/left.
 *
 *  WHY THIS EXISTS: the allocate popover used to render `position:absolute`
 *  INSIDE the email reader, which nests it in three stacked
 *  `overflow-hidden` / `overflow-y-auto` scroll containers. On the mobile
 *  reader that clipped it out of sight — operator (2026-06-16): clicking
 *  "Add to board" did "still nothing". Portalling to <body> with a computed
 *  fixed position is the only clip-proof anchor. */
export function computePopoverPosition(
  trigger: { top: number; bottom: number; left: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 4,
  margin = 8,
): { top: number; left: number } {
  const spaceBelow = viewport.height - trigger.bottom
  const flipUp = spaceBelow < size.height + gap && trigger.top > size.height + gap
  const top = flipUp
    ? Math.max(margin, trigger.top - size.height - gap)
    : trigger.bottom + gap
  const maxLeft = Math.max(margin, viewport.width - size.width - margin)
  const left = Math.min(Math.max(margin, trigger.left), maxLeft)
  return { top, left }
}

export interface AllocateChipProps {
  emailId: string
  api: KanbanApi
  orgId: number | null
  /** Called after allocate/link succeeds. Prefer patching
   *  `allocation_summary` from the optional summary arg (avoids inbox
   *  list refetch + scroll jump). Legacy hosts may ignore the arg and
   *  refetch; omit summary on conflict/fallback paths. */
  onAllocated?: (summary?: EmailAllocationSummary) => void
  /** Change #269 P5 — 409 seam: fires when the allocate hits an existing
   *  placement conflict. `resolved` mirrors the quick-allocate outcome (the
   *  hook may have auto-linked the existing card onto the requested lane).
   *  Hosts use the conflict's card_id to OPEN the existing placement instead
   *  of dead-ending on a toast. Optional — legacy call sites unchanged. */
  onConflict?: (conflict: KanbanAllocationConflict, resolved: boolean) => void
  /** Trigger copy. Defaults to "Add to board". */
  label?: string
  /** Keyboard dialog: render the picker inline, with no pill trigger. */
  embedded?: boolean
  /** Multi-placement (2026-07-09 "multi-lane can't do it"): allocate
   *  as an ADDITIONAL placement — the BE creates a second card on the
   *  picked lane instead of 409ing when the email is already filed
   *  elsewhere. Used by the reader's "+ lane" chip next to an existing
   *  allocation. */
  allowAdditional?: boolean
}

export function AllocateChip({
  emailId,
  api,
  orgId,
  onAllocated,
  onConflict,
  label = 'Add to board',
  allowAdditional = false,
  embedded = false,
}: AllocateChipProps) {
  const [open, setOpen] = useState(embedded)
  // 'new' = create a card on a lane; 'existing' = link to a found card.
  const [mode, setMode] = useState<AllocateMode>('new')
  // Two-step picker: pick a board, then a lane on that board.
  const [board, setBoard] = useState<AllocateMenuBoard | null>(null)
  // FEAT-2026-06-15-ALLOCATE-CREATE-BOARD — "+ New board" dialog state.
  const [createOpen, setCreateOpen] = useState(false)
  // Ref mirror so the once-bound outside-click handler reads the latest
  // dialog state without re-subscribing on every toggle.
  const createOpenRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // Trigger + portalled popover refs. The popover renders in a <body>
  // portal (not inside containerRef) so the outside-click test below must
  // consult BOTH. Anchor holds its computed fixed position.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    createOpenRef.current = createOpen
  }, [createOpen])

  // Recompute the popover's fixed position from the live trigger rect.
  // Runs on open and on scroll/resize so it tracks the chip while the
  // reader scrolls underneath the body portal.
  const reposition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const size = {
      width: popoverRef.current?.offsetWidth || 240,
      height: popoverRef.current?.offsetHeight || 300,
    }
    setAnchor(
      computePopoverPosition(
        { top: r.top, bottom: r.bottom, left: r.left },
        size,
        { width: window.innerWidth, height: window.innerHeight },
      ),
    )
  }, [])

  useEffect(() => {
    if (!open) {
      setAnchor(null)
      return
    }
    reposition()
    // capture:true so we also catch scrolls on the inner reader columns,
    // not just window scroll.
    const onMove = () => reposition()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, board, reposition])

  const allocate = useKanbanQuickAllocate({
    api,
    orgId,
    notify: (kind, message) => toast(message, kind),
  })

  // FEAT-2026-06-24-EMAIL-LINK-EXISTING-CARD — the link-to-existing-card
  // side of the popover. Same toast bridge as `allocate`.
  const link = useKanbanLinkEmailToCard({
    api,
    orgId,
    notify: (kind, message) => toast(message, kind),
  })

  const { createBoard } = useCreateBoardFromPicker({ api })

  // Close on outside click — mirrors AllocationChip's dropdown.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  // Create-board dialog stays inside via [data-kanban-overlay] in shouldDismissContextMenu.
  useCloseOnOutsidePointer(open && !embedded, () => {
    if (createOpenRef.current) return
    setOpen(false)
  }, [containerRef, popoverRef])

  // Never strand the operator inside the lane step (or the 'existing'
  // mode) after a close+reopen — reset to the default board step.
  useEffect(() => {
    if (!open) {
      setBoard(null)
      setMode('new')
    }
  }, [open])

  // Create a board mid-allocation, then drop straight into its lane step
  // so the operator finishes the allocation they came here for. Errors
  // propagate so CreateBoardDialog can surface them inline.
  const handleCreateBoard = useCallback(
    async (input: CreateBoardInput): Promise<{ id: string }> => {
      const newBoard = await createBoard(input)
      setBoard(newBoard)
      return { id: newBoard.id }
    },
    [createBoard],
  )

  const handlePickLane = useCallback(
    async (lane: Lane, boardMeta: AllocateMenuBoard) => {
      const outcome = await allocate.run({
        emailId,
        laneId: lane.id,
        boardId: boardMeta.id,
        source: 'USER_MANUAL',
        allowAdditional: allowAdditional || undefined,
      })
      if (outcome.kind === 'ok') {
        setOpen(false)
        onAllocated?.(
          buildSummaryFromAllocateResult({
            result: outcome.result,
            board: {
              id: boardMeta.id,
              name: boardMeta.name,
              swatch: boardMeta.swatch,
              kind: boardMeta.board?.kind ?? null,
            },
            lane: {
              id: lane.id,
              name: lane.name,
              kind: lane.kind,
              color: lane.color || getLaneColor(lane),
            },
          }),
        )
        return
      }
      if (outcome.kind === 'conflict') {
        // Placement may have resolved; host falls back to refetch when
        // no summary is provided. The conflict seam lets the Placement
        // Manager open the EXISTING placement (plan: 409 duplicate opens
        // the existing placement, change #269 P5).
        setOpen(false)
        onConflict?.(outcome.conflict, outcome.resolved === true)
        onAllocated?.(undefined)
        return
      }
      // BUG-2026-06-21-EMAIL-ALLOCATE-SILENT-FAIL — on a non-writable
      // ("private") board the allocate 403s. Previously the popover just
      // stayed open with no in-context feedback (and `busy` produced NO
      // toast at all), so the operator read it as "nothing happened".
      // Keep the popover open AND surface the reason: the hook already
      // toasts `error` via notify; the `busy` race needs its own nudge,
      // and `allocate.error` renders inline below the list as a durable
      // hint that doesn't vanish like a toast.
      if (outcome.kind === 'busy') {
        toast(outcome.message, 'info')
      }
    },
    [allocate, emailId, onAllocated, onConflict, toast, allowAdditional],
  )

  // FEAT-2026-06-24-EMAIL-LINK-EXISTING-CARD — link this email onto an
  // existing card picked from the quick-search list. On success we close
  // the popover and let the host repaint (the email now shows linked).
  const handleLinkCard = useCallback(
    async (card: CardSearchResult) => {
      const outcome = await link.run({
        cardId: card.id,
        emailId,
        boardId: card.board_id,
        source: 'USER_MANUAL',
      })
      if (outcome.kind === 'ok') {
        setOpen(false)
        onAllocated?.(
          buildSummaryFromLinkResult({
            result: outcome.result,
            card,
          }),
        )
      }
      // 'error' already toasts + sets link.error (rendered inline below
      // the search list); 'busy' is a transient double-click guard.
    },
    [link, emailId, onAllocated],
  )

  // Switch modes from the segmented toggle. Clearing `board` keeps the
  // 'new' flow from re-opening straight into a stale lane step.
  const handleSetMode = useCallback((next: AllocateMode) => {
    setMode(next)
    setBoard(null)
  }, [])

  const pickerInner = (
    <>
      {(mode === 'existing' || !board) && (
        <AllocateModeToggle mode={mode} onChange={handleSetMode} />
      )}
      {mode === 'existing' ? (
        <CardSearchList
          api={api}
          organisationId={orgId}
          busy={link.busy}
          linkError={link.error}
          onPick={(card) => void handleLinkCard(card)}
        />
      ) : board ? (
        <LanePickList
          api={api}
          board={board}
          busy={allocate.busy}
          onBack={() => setBoard(null)}
          onPick={(lane) => void handlePickLane(lane, board)}
        />
      ) : (
        <BoardPickList
          api={api}
          orgId={orgId}
          busy={allocate.busy}
          onPick={(b) => setBoard(b)}
          onCreateBoard={orgId != null ? () => setCreateOpen(true) : undefined}
        />
      )}
      {allocate.error && (
        <div
          className="border-t border-border mt-1 px-2 py-1 text-[11px] text-red-500"
          role="alert"
          data-testid="allocate-error"
        >
          {allocate.error}
        </div>
      )}
    </>
  )

  return (
    <div ref={containerRef} className="relative inline-flex max-w-full min-w-0">
      {!embedded && (
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          // Clear any stale failure from a prior attempt as we reopen, so
          // the popover never shows an error left over from last time.
          if (!open) {
            allocate.reset()
            link.reset()
          }
          setOpen((o) => !o)
        }}
        disabled={allocate.busy}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-dashed border-border',
          'px-2 py-0.5 text-[10px] font-medium text-muted-foreground',
          'hover:bg-accent/50 hover:text-foreground transition-colors',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          allocate.busy && 'opacity-60 cursor-wait',
        )}
        title="Allocate this email to a Kanban board + lane"
        data-testid="email-allocate-chip"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Kanban className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <Plus className="h-2.5 w-2.5 shrink-0" aria-hidden />
        <span>{label}</span>
      </button>
      )}

      {open && embedded && (
        <div
          ref={popoverRef}
          className="min-w-[220px] max-w-[280px] rounded-md border border-border bg-card p-1"
          onClick={(e) => e.stopPropagation()}
          role="menu"
          data-testid="email-allocate-picker"
        >
          {pickerInner}
        </div>
      )}

      {/* Portalled to <body> so the menu can NEVER be clipped by the
          reader's nested overflow-hidden / overflow-y-auto scroll
          containers (BUG-2026-06-16-ALLOCATE-POPOVER-CLIPPED — clicking
          "Add to board" did nothing visible on the mobile reader). Fixed
          position is anchored to the trigger via computePopoverPosition.
          `invisible` for the first paint (anchor null) so the browser can
          measure offsetHeight before we place it (avoids a flicker at 0,0). */}
      {open && !embedded &&
        createPortal(
          <div
            ref={popoverRef}
            className={cn(
              // z-[80]: above Placement Manager backdrop/dialog (70/71).
              // BUG-2026-08-03-PLACEMENT-MGR-ALLOCATE-UNDER-BACKDROP
              'fixed min-w-[220px] max-w-[260px] rounded-md border border-border bg-card shadow-lg p-1',
              ALLOCATION_MENU_Z_CLASS,
              !anchor && 'invisible',
            )}
            style={anchor ? { top: anchor.top, left: anchor.left } : { top: 0, left: 0 }}
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            {pickerInner}
          </div>,
          document.body,
        )}

      {/* "+ New board" — create a board without leaving the allocate
          flow. Needs an org to scope the board; the button is hidden
          when orgId is unknown (see BoardPickList footer gate). */}
      {orgId != null && (
        <CreateBoardDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          organisationId={orgId}
          onCreate={handleCreateBoard}
          loadTemplateCandidates={() =>
            api.listBoards(orgId != null ? { organisationId: orgId } : undefined)
          }
        />
      )}
    </div>
  )
}
